import { useCallback, useMemo } from 'react';

import { QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';

import { collectionsEndpoints as endpoints } from '@/api/endpoints';
import { callDelete, callGet, callPost, callPut } from '@/api/fetcher';
import { getPermissions } from '@/redux/permissions/selectors';
import { selectAccountId } from '@/redux/session/selectors';
import { Collection, Collections, PartialCollection } from '@/shared/types/collections';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';
import { isLimitReached } from '@/util/limits';

import { useAccount } from './useAccount';
import { createEntityCache } from './utils/entityCache';

interface DeleteProps {
  collection_id: string;
  force: boolean;
}

export const API = Object.freeze({
  listAllCollections: async (): Promise<Collections> => callGet(endpoints.collections),

  getCollection: async (collectionId: string): Promise<Collection> =>
    callGet(endpoints.collection(collectionId)),

  createCollection: async (newCollection: Partial<Collection>): Promise<Collection> =>
    callPost(endpoints.collections, newCollection),

  updateCollection: async ({
    collection_id,
    ...collection
  }: PartialCollection): Promise<Collection> =>
    callPut(endpoints.collection(collection_id), collection),

  deleteCollection: async (collectionId: string, force?: boolean): Promise<Collection> => {
    const queryParams = force ? `?force=${force}` : '';
    return callDelete(`${endpoints.collection(collectionId)}${queryParams}`);
  },
});

export const collectionCache = createEntityCache<Collection, 'collections'>({
  listKey: () => [endpoints.collections],
  detailKey: (_parentId, collectionId) => [endpoints.collection(collectionId)],
  listField: 'collections',
  getId: (collection) => collection.collection_id,
  insertAt: 'start',
});

export const invalidateCollections = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({ queryKey: [endpoints.collections] });
};

export const onRTMCollectionUpdated = async (queryClient: QueryClient, collection_id: string) => {
  try {
    const collection = await API.getCollection(collection_id);
    collectionCache.updated(queryClient, collection);
  } catch (error) {
    // Silently fail as requested
    console.error('Failed to fetch collection during RTM update:', error);
  }
};

const useCollections = (collectionId?: string) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const accountId = useSelector(selectAccountId);
  const { account } = useAccount();

  const canRead = useSelector((state: RootState) =>
    getPermissions(state, 'collections', actions.READ)
  );

  const {
    data: collections,
    status: listStatus,
    isLoading: isCollectionsLoading,
  } = useQuery<Collections, Error>({
    queryKey: [endpoints.collections],
    queryFn: () => API.listAllCollections(),
    enabled: !!accountId && canRead,
    staleTime: 2 * 60 * 1000, // 2 minutes in milliseconds
  });

  const { data: collection, status: getStatus } = useQuery<Collection, Error>({
    queryKey: [endpoints.collection(collectionId)],
    queryFn: () => API.getCollection(collectionId),
    enabled: !!collectionId && canRead,
    staleTime: 2 * 60 * 1000, // 2 minutes in milliseconds
  });

  const { mutate: updateCollection, status: updateStatus } = useMutation<
    Collection,
    Error,
    PartialCollection
  >({
    mutationFn: API.updateCollection,
    onSuccess: (resp) => {
      collectionCache.updated(queryClient, resp);
      toast.success(t('collections.updated', { 0: resp.name }));
    },
    meta: { errorToast: true },
  });

  const { mutate: createCollection, status: createStatus } = useMutation<
    Collection,
    Error,
    Partial<Collection>
  >({
    mutationFn: API.createCollection,
    onSuccess: (resp) => {
      collectionCache.created(queryClient, resp);
      toast.success(t('collections.created', { 0: resp?.name }));
    },
    meta: { errorToast: true },
  });

  const {
    mutate: deleteCollection,
    mutateAsync: deleteCollectionAsync,
    status: deleteStatus,
  } = useMutation<Collection, Error, DeleteProps>({
    mutationFn: ({ collection_id, force }) => API.deleteCollection(collection_id, force),
    onSuccess: (resp) => {
      collectionCache.removed(queryClient, { id: resp.collection_id });
      toast.success(t('collections.deleted'));
    },
    meta: { errorToast: true },
  });

  const collectionsByLanguage = useCallback(
    (language: string) => {
      return collections?.collections?.filter((c) => c.language === language);
    },
    [collections?.collections]
  );

  const collectionNames = useMemo(
    () => collections?.collections?.map((c) => c.name.toLowerCase()),
    [collections?.collections]
  );

  const totalFragments = useMemo(
    () => collections?.collections?.reduce((acc, col) => acc + col.fragment_count, 0),
    [collections?.collections]
  );
  return {
    totalCount: collections?.pagination?.total,
    collections: collections?.collections,
    collection,
    maxCollections: account?.max_collections,
    maxCollectionsReached: isLimitReached(
      collections?.pagination?.total ?? 0,
      account?.max_collections
    ),
    totalFragments,
    totalFragmentsReached: isLimitReached(totalFragments ?? 0, account?.max_fragments),
    getStatus,
    isCollectionsLoading,
    listStatus,
    createStatus,
    deleteStatus,
    updateStatus,
    collectionNames,
    createCollection,
    deleteCollection,
    deleteCollectionAsync,
    updateCollection,
    collectionsByLanguage,
  };
};

export default useCollections;
